import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, Car, DollarSign, X, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['exotic', 'supercar', 'sport', 'luxury', 'suv', 'ev', 'muscle', 'truck'];
const PARTNERS = ['ace'];
const RATE_TYPES = ['daily', 'request'];

const AdminRentalPricing = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ vehicle: '', category: 'sport', partner: 'ace', retail_rate: 0, rate_type: 'daily', note: '' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const loadFleet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/rentals/admin/fleet`);
      const data = await res.json();
      setVehicles(data.vehicles || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadFleet(); }, [loadFleet]);

  const addVehicle = async () => {
    if (!newVehicle.vehicle) return;
    setSaving('add');
    await fetch(`${API}/api/rentals/admin/vehicle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newVehicle)
    });
    toast.success('Vehicle added');
    setShowAdd(false);
    setNewVehicle({ vehicle: '', category: 'sport', partner: 'ace', retail_rate: 0, rate_type: 'daily', note: '' });
    setSaving(null);
    loadFleet();
  };

  const saveEdit = async (rentalId) => {
    setSaving(rentalId);
    await fetch(`${API}/api/rentals/admin/vehicle/${rentalId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData)
    });
    toast.success('Pricing updated');
    setEditId(null);
    setSaving(null);
    loadFleet();
  };

  const deleteVehicle = async (rentalId) => {
    if (!window.confirm('Delete this vehicle?')) return;
    await fetch(`${API}/api/rentals/admin/vehicle/${rentalId}`, { method: 'DELETE' });
    toast.success('Deleted');
    loadFleet();
  };

  const startEdit = (v) => {
    setEditId(v.rental_id);
    setEditData({ vehicle: v.vehicle, category: v.category, partner: v.partner, retail_rate: v.retail_rate, rate_type: v.rate_type, available: v.available, note: v.note || '' });
  };

  const aceVehicles = vehicles.filter(v => v.partner === 'ace');

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-red-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-rental-pricing">
      {/* Header */}
      <div className="bg-black text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><DollarSign className="h-5 w-5 text-red-500" /> Rental Pricing Manager</h1>
              <p className="text-sm text-gray-400">Edit daily rental rates for the self-drive fleet</p>
            </div>
          </div>
          <Button onClick={() => setShowAdd(true)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid="add-vehicle-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Vehicle
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Add Vehicle Form */}
        {showAdd && (
          <Card className="p-6 border-red-200 bg-red-50" data-testid="add-vehicle-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Add New Rental Vehicle</h3>
              <button onClick={() => setShowAdd(false)}><X className="h-4 w-4 text-gray-400" /></button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input value={newVehicle.vehicle} onChange={e => setNewVehicle(p => ({ ...p, vehicle: e.target.value }))} placeholder="Vehicle Name" className="text-sm h-9" />
              <select value={newVehicle.category} onChange={e => setNewVehicle(p => ({ ...p, category: e.target.value }))} className="h-9 px-3 rounded-md border text-sm">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={newVehicle.partner} onChange={e => setNewVehicle(p => ({ ...p, partner: e.target.value }))} className="h-9 px-3 rounded-md border text-sm">
                {PARTNERS.map(p => <option key={p} value={p}>{p === 'ace' ? 'ACE Fleet' : p}</option>)}
              </select>
              <select value={newVehicle.rate_type} onChange={e => setNewVehicle(p => ({ ...p, rate_type: e.target.value }))} className="h-9 px-3 rounded-md border text-sm">
                {RATE_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <Input type="number" value={newVehicle.retail_rate} onChange={e => setNewVehicle(p => ({ ...p, retail_rate: Number(e.target.value) }))} placeholder="Daily Rate $" className="text-sm h-9" />
              <Input value={newVehicle.note} onChange={e => setNewVehicle(p => ({ ...p, note: e.target.value }))} placeholder="Notes (optional)" className="text-sm h-9" />
              <Button onClick={addVehicle} disabled={!newVehicle.vehicle || saving === 'add'} className="bg-green-600 hover:bg-green-700 text-xs h-9">
                {saving === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
              </Button>
            </div>
          </Card>
        )}

        {/* Fleet Tables */}
        {[
          { title: 'ACE Self-Drive Fleet', items: aceVehicles, partner: 'ace' },
        ].map(group => (
          <Card key={group.partner} className="overflow-hidden">
            <div className="bg-gray-900 text-white px-4 py-3 flex items-center gap-2">
              <Car className="h-4 w-4 text-red-500" />
              <h3 className="font-bold text-sm">{group.title}</h3>
              <span className="text-xs text-gray-400 ml-auto">{group.items.length} vehicles</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`table-${group.partner}`}>
                <thead className="bg-gray-100 text-gray-600 text-xs">
                  <tr>
                    <th className="text-left px-4 py-2">Vehicle</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-right px-4 py-2">Daily Rate</th>
                    <th className="text-center px-4 py-2">Type</th>
                    <th className="text-center px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Note</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(v => {
                    const isEditing = editId === v.rental_id;
                    return (
                      <tr key={v.rental_id} className={`border-t ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-testid={`row-${v.rental_id}`}>
                        <td className="px-4 py-2.5 font-medium">
                          {isEditing ? <Input value={editData.vehicle} onChange={e => setEditData(p => ({ ...p, vehicle: e.target.value }))} className="text-sm h-8 w-48" /> : v.vehicle}
                        </td>
                        <td className="px-4 py-2.5 capitalize text-gray-500">
                          {isEditing ? (
                            <select value={editData.category} onChange={e => setEditData(p => ({ ...p, category: e.target.value }))} className="h-8 px-2 rounded border text-xs">
                              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : v.category}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? <Input type="number" value={editData.retail_rate} onChange={e => setEditData(p => ({ ...p, retail_rate: Number(e.target.value) }))} className="text-sm h-8 w-24 text-right" /> : (v.retail_rate > 0 ? `$${v.retail_rate}` : '—')}
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-gray-500">
                          {isEditing ? (
                            <select value={editData.rate_type} onChange={e => setEditData(p => ({ ...p, rate_type: e.target.value }))} className="h-8 px-2 rounded border text-xs">
                              {RATE_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          ) : v.rate_type}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {isEditing ? (
                            <select value={editData.available ? 'yes' : 'no'} onChange={e => setEditData(p => ({ ...p, available: e.target.value === 'yes' }))} className="h-8 px-2 rounded border text-xs">
                              <option value="yes">Available</option>
                              <option value="no">Unavailable</option>
                            </select>
                          ) : (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {v.available ? 'Active' : 'Off'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400 max-w-[150px] truncate">
                          {isEditing ? <Input value={editData.note} onChange={e => setEditData(p => ({ ...p, note: e.target.value }))} className="text-sm h-8 w-32" placeholder="Note" /> : (v.note || '—')}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isEditing ? (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" onClick={() => saveEdit(v.rental_id)} disabled={saving === v.rental_id} className="bg-green-600 hover:bg-green-700 h-7 w-7 p-0">
                                {saving === v.rental_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditId(null)} className="h-7 w-7 p-0"><X className="h-3 w-3" /></Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => startEdit(v)} className="h-7 text-xs px-2" data-testid={`edit-${v.rental_id}`}>Edit</Button>
                              <Button size="sm" variant="outline" onClick={() => deleteVehicle(v.rental_id)} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminRentalPricing;
